import React, { useState, useEffect, useRef, useCallback } from "react";
import { getSurveyWithResponses } from "./serverComm";
import { SurveyAnalyticsTableView } from "./components/SurveyAnalyticsTable";
import SurveyAnalyticsInsight from "./components/SurveyAnalytics/SurveyAnalyticsInsight";
import SurveyAnalyticsOverview from "./components/SurveyAnalytics/SurveyAnalyticsOverview";
import SurveyResearchAssistant from "./components/SurveyAnalytics/SurveyResearchAssistant";
import ResultsSidebar from "./components/SurveyAnalytics/ResultsSidebar";
import ResultsDropdownMobile from "./components/SurveyAnalytics/ResultsDropdownMobile";
import { useDispatch } from "react-redux";
import { setActiveSurvey } from "../../store/appSlice";
import { useNavigate, useLocation } from "react-router-dom";
import { AppLoader } from "./components/AppLoader";
import PermissionNotice from "./components/PermissionNotice";
import { Inbox } from "lucide-react";

const CACHE_TTL_MS = 60_000;

const SurveyAnalytics = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const surveyId = window.location.pathname.split("/")[2];
  const [activeTab, setActiveTab] = useState("Insights");
  const dispatch = useDispatch();
  const [survey, setSurvey] = useState(null);
  const [loading, setLoading] = useState(true);
  const [initialLoadDone, setInitialLoadDone] = useState(false);
  const [hasAnyResponses, setHasAnyResponses] = useState(null);
  const [error, setError] = useState(null);
  const surveyCache = useRef({});

  // Function to get tab from URL hash
  const getTabFromHash = () => {
    const hash = location.hash.replace('#', '');
    const validTabs = ["Overview", "Responses", "Insights", "Research Assistant"];
    // Convert hash to proper tab name (e.g., "insights" -> "Insights")
    const tabMap = {
      'overview': 'Overview',
      'responses': 'Responses', 
      'insights': 'Insights',
      'research-assistant': 'Research Assistant'
    };
    return tabMap[hash] && validTabs.includes(tabMap[hash]) ? tabMap[hash] : "Insights";
  };

  // Function to set active tab and update URL hash
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    const hashMap = {
      'Overview': 'overview',
      'Responses': 'responses',
      'Insights': 'insights', 
      'Research Assistant': 'research-assistant'
    };
    const hash = hashMap[tab];
    // Update URL hash without triggering navigation
    window.location.hash = hash;
  };

  // Initialize tab from URL hash on component mount
  useEffect(() => {
    const initialTab = getTabFromHash();
    setActiveTab(initialTab);
  }, [location.hash]);

  const fetchIdRef = useRef(0);
  const abortRef = useRef(null);

  useEffect(() => {
    fetchSurveyData();
    return () => { abortRef.current?.abort(); };
  }, [surveyId, activeTab]);

  const fetchSurveyData = useCallback(async () => {
    const currentFetchId = ++fetchIdRef.current;
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    const cacheKey = `${surveyId}_${activeTab}`;
    const cached = surveyCache.current[cacheKey];
    if (cached && Date.now() - cached.timestamp < CACHE_TTL_MS) {
      dispatch(setActiveSurvey(cached.data));
      setSurvey(cached.data);
      setLoading(false);
      setInitialLoadDone(true);
      return;
    }

    try {
      if (!initialLoadDone) {
        setLoading(true);
      }
      
      const filters = activeTab === 'Insights' ? { statusFilter: 'completed' } : {};
      const data = await getSurveyWithResponses(surveyId, filters);

      if (currentFetchId !== fetchIdRef.current) return;
      
      if (hasAnyResponses === null) {
        if (data.responses && data.responses.length > 0) {
          setHasAnyResponses(true);
        } else if (activeTab === 'Insights') {
          const unfilteredData = await getSurveyWithResponses(surveyId, {});
          if (currentFetchId !== fetchIdRef.current) return;
          setHasAnyResponses(unfilteredData.responses && unfilteredData.responses.length > 0);
        } else {
          setHasAnyResponses(false);
        }
      }
      
      surveyCache.current[cacheKey] = { data, timestamp: Date.now() };
      dispatch(setActiveSurvey(data));
      setSurvey(data);
    } catch (err) {
      if (currentFetchId !== fetchIdRef.current) return;
      setError({
        status: err.status,
        message: err.status === 403
          ? "You do not have access to these results"
          : "We could not load this survey's results",
      });
    } finally {
      if (currentFetchId === fetchIdRef.current) {
        setLoading(false);
        setInitialLoadDone(true);
      }
    }
  }, [surveyId, activeTab, initialLoadDone, hasAnyResponses, dispatch]);

  // Check if there are no responses - only after we've determined this
  // hasAnyResponses is set during initial load and is based on unfiltered data
  const hasNoResponses = hasAnyResponses === false;

  // Block scrolling when there are no responses
  useEffect(() => {
    if (hasNoResponses) {
      document.body.style.overflow = 'hidden';
      document.documentElement.style.overflow = 'hidden';
      document.body.style.height = '100vh';
      document.documentElement.style.height = '100vh';
    } else {
      document.body.style.overflow = 'auto';
      document.documentElement.style.overflow = 'auto';
      document.body.style.height = 'auto';
      document.documentElement.style.height = 'auto';
    }
    
    // Cleanup on unmount
    return () => {
      document.body.style.overflow = 'auto';
      document.documentElement.style.overflow = 'auto';
      document.body.style.height = 'auto';
      document.documentElement.style.height = 'auto';
    };
  }, [hasNoResponses]);

  return (
    <div className={`bg-white ${hasNoResponses ? 'h-screen overflow-hidden' : 'min-h-[100vh]'}`} style={{ paddingTop: 'var(--navbar-height, 64px)' }}>
      {/* Mobile Dropdown Navigation */}
      <div className="md:hidden border-b border-gray-200 bg-white fixed left-0 right-0 z-30" style={{ top: 'var(--navbar-height, 64px)' }}>
        <ResultsDropdownMobile activeTab={activeTab} onTabChange={handleTabChange} />
      </div>

      <div className={`flex ${hasNoResponses ? 'h-[calc(100vh-5rem)] overflow-hidden' : 'h-[calc(100vh-4rem)]'}`}>
        {/* Left Sidebar - Desktop only */}
        <div className="hidden md:block fixed left-0 top-[65px] bottom-0 w-72 bg-white border-r border-gray-200 z-30">
          <ResultsSidebar activeTab={activeTab} onTabChange={handleTabChange} />
        </div>

        {/* Main Content */}
        <div className={`flex-1 md:ml-72 ${hasNoResponses ? 'overflow-hidden h-[calc(100vh-4rem)]' : 'overflow-x-auto'}`} style={{ paddingTop: 'calc(var(--navbar-height, 64px) + 3.5rem)' }}>
          <div className="md:hidden -mt-20"></div>
          {error ? (
            <PermissionNotice
              eyebrow={error.status === 403 ? "Restricted results" : "Results unavailable"}
              title={error.message}
              description={
                error.status === 403
                  ? "Your current role does not include access to this survey's results. Ask an owner or admin to update your role if you need access."
                  : "This can happen if the survey belongs to another workspace, was deleted, or your role changed. Go back to workspaces and select a survey from your current organization."
              }
              secondaryLabel="Back to workspaces"
              onSecondary={() => navigate("/workspaces")}
            />
          ) : loading || !survey ? (
            <div className="fixed inset-0 bg-white" style={{ paddingTop: 'var(--navbar-height, 64px)' }}>
              <div className="flex justify-center items-center h-full">
                <AppLoader coverage="partial" />
              </div>
            </div>
          ) : hasNoResponses ? (
            /* Empty State - Yellow Container */
            <div className="h-[calc(100vh-4rem)] p-6 pt-0 md:-mt-24 pb-12 overflow-x-hidden overflow-y-auto">
              <div className="bg-[#F0F6FF] border border-[#D6E4FF] rounded-2xl h-full flex overflow-hidden">
                {/* Left: text content */}
                <div className="flex-1 flex flex-col justify-center items-center text-center px-12 py-10">
                  <div className="w-16 h-16 bg-[#DCE9FF] rounded-2xl flex items-center justify-center mb-6">
                    <Inbox className="w-8 h-8 text-blue-500" strokeWidth={1.75} />
                  </div>
                  <h3 className="text-2xl font-semibold text-gray-900 mb-3">
                    No responses collected yet
                  </h3>
                  <p className="text-gray-500 leading-relaxed text-base max-w-md mx-auto mb-8">
                    To unlock insights and analytics, start gathering answers from your form.
                    Once responses start coming in, your results will appear here.
                  </p>
                  <div className="bg-white rounded-xl p-5 border border-[#D6E4FF] max-w-md mx-auto text-left">
                    <p className="text-sm text-gray-900 font-semibold mb-1.5">
                      💡 How to collect responses
                    </p>
                    <p className="text-sm text-gray-500 leading-relaxed">
                      Share your survey link on social media, email, or embed it on your website to start collecting responses.
                    </p>
                  </div>
                </div>
                {/* Right: iPad mockup glued flush to the right edge (no gap) */}
                <div className="hidden lg:block self-stretch flex-shrink-0">
                  <img
                    src="https://firebasestorage.googleapis.com/v0/b/surveyappreact.firebasestorage.app/o/default%2Fipad-.png?alt=media&token=eb201c68-61c6-46aa-8120-56b052db2ebe"
                    alt="Analytics dashboard preview"
                    className="h-full w-auto object-contain object-right select-none pointer-events-none"
                    draggable={false}
                  />
                </div>
              </div>
            </div>
          ) : (
            /* Regular Content */
            <>
              {activeTab === "Overview" && (
                <SurveyAnalyticsInsight surveyId={surveyId} survey={survey} />
              )}
              {activeTab === "Responses" && (
                <SurveyAnalyticsTableView surveyId={surveyId} />
              )}
              {activeTab === "Insights" && (
                <SurveyAnalyticsOverview surveyId={surveyId} survey={survey} />
              )}
              {activeTab === "Research Assistant" && (
                <SurveyResearchAssistant surveyId={surveyId} survey={survey} />
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SurveyAnalytics;
